import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Fingerprint, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { auth } from '../services/api';
import { getVersionString } from '../config/version';
import { setPendingPassword } from '../utils/pendingPasswordChange';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accountLocked, setAccountLocked] = useState(false);
  const [lockoutMinutes, setLockoutMinutes] = useState(0);

  // Show messages from navigation state (e.g., session timeout, locked session)
  useEffect(() => {
    if (location.state?.message) {
      const type = location.state?.type || 'info';
      if (type === 'error') {
        setError(location.state.message);
      } else {
        setInfo(location.state.message);
      }
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setAccountLocked(false);
    setLoading(true);

    try {
      const response = await auth.login(formData.email, formData.password);

      // Check if user must change password
      // Note: auth.login returns { token, user } directly
      if (response.user?.mustChangePassword) {
        setPendingPassword(formData.password);
        navigate('/change-password', {
          state: {
            required: true,
            message: 'Por favor, atualize sua senha para atender aos novos requisitos de segurança.'
          }
        });
        return;
      }

      // Normal login - go to dashboard
      navigate('/dashboard');

    } catch (err) {
      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.message;

      // Handle specific error codes from backend
      if (errorCode === 'ACCOUNT_LOCKED') {
        setAccountLocked(true);
        const minutes = Math.ceil((new Date(err.response.data.lockedUntil) - new Date()) / (1000 * 60));
        setLockoutMinutes(minutes > 0 ? minutes : 0);
        setError(errorMessage || `Conta bloqueada. Tente novamente em ${minutes} minutos.`);
      } else if (errorCode === 'INVALID_CREDENTIALS') {
        const errorMsg = errorMessage || 'Credenciais inválidas. Por favor, verifique seu email e senha.';
        setError(errorMsg);
      } else if (errorCode === 'SESSION_TIMEOUT' || errorCode === 'SESSION_EXPIRED') {
        setInfo('Sua sessão expirou. Por favor, faça login novamente.');
      } else {
        setError(errorMessage || err.message || 'Falha no login. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-100 via-brand-50 to-coral-100">
      <div className="backdrop-blur-lg bg-white/70 rounded-2xl shadow-2xl p-10 w-full max-w-md flex flex-col items-center">
        {/* Logo */}
        <div className="mb-6 flex flex-col items-center">
          <img src="/brand/gia-logo-stacked.svg" alt="GiA - Guia Inteligente do Autismo" className="h-56 mb-2" />
          <p className="text-xs text-gray-500 mt-2">{getVersionString()}</p>
        </div>
        <form className="w-full space-y-5" onSubmit={handleSubmit}>
          {/* Info Message (session timeout, etc.) */}
          {info && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md text-sm mb-2 flex items-start">
              <Info className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{info}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className={`border px-4 py-3 rounded-md text-sm mb-2 flex items-start ${
              accountLocked
                ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p>{error}</p>
                {accountLocked && lockoutMinutes > 0 && (
                  <p className="mt-2 text-xs">
                    Para proteger sua conta, ela foi bloqueada temporariamente após múltiplas tentativas de login falhas.
                    Você poderá tentar novamente em aproximadamente {lockoutMinutes} minuto{lockoutMinutes !== 1 ? 's' : ''}.
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-col space-y-4">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </span>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="block w-full rounded-lg border border-gray-300 pl-10 pr-3 py-3 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 bg-white/80"
                placeholder="admin@example.com"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </span>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="block w-full rounded-lg border border-gray-300 pl-10 pr-10 py-3 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 bg-white/80"
                placeholder="Senha"
                value={formData.password}
                onChange={handleChange}
                autoComplete="off"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2">
            <label className="flex items-center text-sm text-gray-700">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded mr-2"
              />
              Lembrar-me
            </label>
            <a href="#" className="text-sm text-brand-600 hover:text-brand-800 font-medium">Esqueceu sua senha?</a>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="mt-4 w-full flex items-center justify-center py-3 px-4 rounded-lg bg-brand-700 hover:bg-brand-800 text-white text-lg font-semibold shadow-md transition-all duration-150 disabled:bg-brand-400"
          >
            <Fingerprint className="h-6 w-6 mr-2" />
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login; 